/**
 * @file Click-to-copy identifiers — device names, serials, phone numbers,
 * emails, employee numbers.
 *
 * Contract: clicking the text copies the RAW value (not the display text —
 * "SN SD67VJ6DDGG" copies "SD67VJ6DDGG") and confirms with a toast. The
 * click stops propagating, so an enclosing row/cell keeps its own click
 * (navigate to the detail page) for everything outside the copyable text.
 * A manual drag-selection inside the span is respected (no copy on mouseup).
 * Keyboard: Enter / Space.
 *
 * Visual: a dotted underline on hover (style.css .copyable) — deliberately no
 * special cursor — so it reads as actionable without looking like a link,
 * which navigates.
 */

import { el, toast } from '../utils.js';

const MONO = 'font-family:var(--font-serial);font-size:var(--fs-small)';
// Touch screens have no hover, and a row's name/serial is most of its tap
// area — so inside a tappable row (`[data-row-link]`) a tap opens the row.
const TOUCH = typeof matchMedia === 'function' ? matchMedia('(hover: none) and (pointer: coarse)') : null;

export async function copyToClipboard(text) {
  const value = String(text ?? '');
  if (!value) return false;
  try {
    if (navigator.clipboard?.writeText) { await navigator.clipboard.writeText(value); return true; }
  } catch { /* fall through to the legacy path */ }
  try {
    const ta = document.createElement('textarea');
    ta.value = value;
    ta.setAttribute('readonly', '');
    ta.style.position = 'fixed';
    ta.style.opacity = '0';
    document.body.appendChild(ta);
    ta.select();
    const ok = document.execCommand('copy');
    ta.remove();
    return ok;
  } catch {
    return false;
  }
}

/**
 * @param value          what gets copied
 * @param opts.label     noun for the title + toast ("SN", "email", "number")
 * @param opts.display   what to SHOW if different from `value` (e.g. "SN X")
 * @param opts.mono      monospace identifier styling (class "sn")
 * @param opts.class     extra classes
 * @param opts.style     extra inline style
 */
export function copyable(value, { label = null, display = null, mono = false, class: cls = '', style = '' } = {}) {
  const text = String(value ?? '');
  const classes = ['copyable', mono ? 'sn' : '', cls].filter(Boolean).join(' ');
  const styles = [mono ? MONO : '', style].filter(Boolean).join(';');
  const node = el('span', {
    class: classes,
    style: styles || null,
    role: 'button',
    tabindex: '0',
    title: `Click to copy${label ? ` ${label}` : ''}`,
  }, display ?? text);

  const doCopy = async (e) => {
    // Touch devices: inside a tappable row the row wins — the tap opens the
    // person/device instead of copying. Copy stays on detail pages and
    // anywhere that isn't a row link; keyboard copy always works.
    if (e.type === 'click' && TOUCH?.matches && node.closest('[data-row-link]')) return;
    e.stopPropagation();
    e.preventDefault();
    // The user dragged to select part of the text — let them have their
    // selection instead of clobbering the clipboard with the whole value.
    const sel = window.getSelection?.();
    if (sel && !sel.isCollapsed && node.contains(sel.anchorNode)) return;
    const ok = await copyToClipboard(text);
    const short = text.length > 48 ? `${text.slice(0, 45)}…` : text;
    toast(ok
      ? `Copied${label ? ` ${label}` : ''} “${short}” to clipboard.`
      : 'Could not copy — the browser blocked clipboard access.', ok ? 'success' : 'error');
  };
  node.addEventListener('click', doCopy);
  node.addEventListener('keydown', (e) => { if (e.key === 'Enter' || e.key === ' ') doCopy(e); });
  return node;
}
